import { Injectable } from "@angular/core";
import { App } from "@capacitor/app";
import { Capacitor } from "@capacitor/core";
import {
  Geolocation,
  GeolocationOptions,
  GeolocationPosition,
  Position,
  PositionOptions,
} from "@capacitor/geolocation";
import { Observable, Subject, Subscription, interval } from "rxjs";
import { LocalStorageService } from "./localStorage.service";
import { osService } from "./osService.service";
import { PusherService } from "./pusher.service";

@Injectable({
  providedIn: "root",
})
export class GeoLocationService {
  previousLocation: { lat: number; lng: number } | null = null;
  lastNotificationTime: number = 0;
  notificationInterval: number = 1000 * 10; // milliseconds
  distanceThreshold: number = 30; // 30 meters
  locationSubject: Subject<any> = new Subject<any>();
  private intervalSubscription!: Subscription;
  private appStateListener: any;

  constructor(
    private osService: osService,
    private pusherService: PusherService,
    private localStorageService: LocalStorageService
  ) {
    this.setupAppStateListener();
  }

  /** Sur iOS (et mobile), au retour au premier plan, redémarre le tracking position (timer suspendu en arrière-plan). */
  private setupAppStateListener(): void {
    if (!Capacitor.isNativePlatform()) {
      return;
    }
    try {
      this.appStateListener = App.addListener(
        "appStateChange",
        (state: { isActive?: boolean }) => {
          const isActive = state?.isActive === true;
          const role = this.localStorageService.getUserRole();
          if (role !== "prestataire") return;
          if (isActive) {
            this.lastNotificationTime = 0;
            this.previousLocation = null; // prochain handleLocationChange enverra si getCurrentPosition réussit
            this.startTracking();
          } else {
            // Arrière-plan : arrêter l'intervalle Ionic (getCurrentPosition échoue en background)
            // Le tracking natif iOS/Android continue et envoie les positions
            this.stopTracking();
          }
        }
      );
    } catch (e) {
      console.warn("[GeoLocationService] App state listener setup failed", e);
    }
  }

  async checkLocationPermission(): Promise<boolean> {
    if (Capacitor.isNativePlatform()) {
      try {
        const permissionStatus = await Geolocation.checkPermissions();
        return permissionStatus.location === "granted";
      } catch (error) {
        console.error("Error checking location permission", error);
        return false;
      }
    } else {
      if (!("geolocation" in navigator)) {
        return false;
      }
      if ("permissions" in navigator) {
        try {
          const result = await navigator.permissions.query({ name: "geolocation" as PermissionName });
          return result.state === "granted";
        } catch {
          // Permissions API not supported — fall through to position attempt
        }
      }
      // Fallback: try to get position to determine real permission state
      return new Promise((resolve) => {
        navigator.geolocation.getCurrentPosition(
          () => resolve(true),
          () => resolve(false),
          { timeout: 3000, maximumAge: Infinity }
        );
      });
    }
  }
  async requestLocationPermission(): Promise<boolean> {
    if (Capacitor.isNativePlatform()) {
      try {
        const permission = await Geolocation.requestPermissions();
        return permission.location === "granted";
      } catch (error) {
        console.error("Error requesting location permission", error);

        return false;
      }
    } else {
      return new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(
          () => resolve(true),
          (error) => {
            console.error("Error requesting location permission on web", error);
            resolve(false);
          }
        );
      });
    }
  }

  sendPosition(options: GeolocationOptions) {
    const auth = this.localStorageService.isAuth();
    if (!auth) {
      return;
    }
    this.trackLocation(options);
  }
  async startTracking() {
    const auth = this.localStorageService.isAuth();
    const role = this.localStorageService.getUserRole();
    if (!auth) {
      return;
    }
    if (role !== "prestataire") {
      return;
    }

    if (Capacitor.isNativePlatform()) {
      try {
        const permStatus = await Geolocation.checkPermissions();
        if (permStatus.location === "denied" || permStatus.location === "prompt" || permStatus.location === "prompt-with-rationale") {
          console.warn("[GeoLocationService] startTracking skipped — location permission not granted:", permStatus.location);
          return;
        }
      } catch {
        return;
      }
    }

    this.stopTracking();
    this.lastNotificationTime = 0; // forcer un envoi dès reprise
    const options: GeolocationOptions = {
      enableHighAccuracy: true,
      maximumAge: 0,
      timeout: 30000,
    };
    this.sendPosition(options);
    this.intervalSubscription = interval(20000).subscribe(() => {
      this.sendPosition(options);
    });
  }

  async trackLocation(options: GeolocationOptions) {
    let coordinates: GeolocationPosition;
    try {
      coordinates = await Geolocation.getCurrentPosition(options);
    } catch (err) {
      console.warn("[GeoLocationService] getCurrentPosition failed", err);
      return;
    }
    this.handleLocationChange(
      coordinates.coords.latitude,
      coordinates.coords.longitude
    );
    this.locationSubject.next({
      lat: coordinates.coords.latitude,
      lng: coordinates.coords.longitude,
    });
  }
  async getCurrentPosition() {
    const options: PositionOptions = {
      enableHighAccuracy: true,
      maximumAge: 0,
      timeout: 30000,
    };
    const coordinates: Position = await Geolocation.getCurrentPosition(options);
    return {
      lat: coordinates.coords.latitude,
      lng: coordinates.coords.longitude,
    };
  }

  handleLocationChange(lat: number, lng: number) {
    const user = this.localStorageService.getToken();
    const role = this.localStorageService.getUserRole();
    if (!user || role !== "prestataire") {
      return;
    }
    const now = Date.now();
    const timeSinceLast = now - this.lastNotificationTime;
    if (this.previousLocation) {
      const distanceMoved = this.calculateDistance(
        this.previousLocation.lat,
        this.previousLocation.lng,
        lat,
        lng,
        "m"
      );
      const shouldSend =
        distanceMoved > this.distanceThreshold ||
        timeSinceLast > this.notificationInterval;

      if (shouldSend) {
        this.osService
          .savePrestatairePosition({ lat: lat, long: lng })
          .subscribe({
            next: (res: any) => {
              this.pusherService.dispatchDistance(res);
              this.previousLocation = { lat, lng };
              this.lastNotificationTime = Date.now();
            },
            error: (err) =>
              console.warn("[GeoLocationService] savePrestatairePosition error", err),
          });
        this.lastNotificationTime = now;
      }
    } else {
      this.osService
        .savePrestatairePosition({ lat: lat, long: lng })
        .subscribe({
          next: (res: any) => {
            this.pusherService.dispatchDistance(res);
            this.previousLocation = { lat, lng };
            this.lastNotificationTime = Date.now();
          },
          error: (err) =>
            console.warn("[GeoLocationService] savePrestatairePosition error", err),
        });
      this.lastNotificationTime = now;
    }
  }

  calculateDistance(
    lat1: number,
    lon1: number,
    lat2: number,
    lon2: number,
    unit: "m" | "km" = "m"
  ): number {
    const R = unit === "km" ? 6371e3 : 6371;
    const φ1 = (lat1 * Math.PI) / 180;
    const φ2 = (lat2 * Math.PI) / 180;
    const Δφ = ((lat2 - lat1) * Math.PI) / 180;
    const Δλ = ((lon2 - lon1) * Math.PI) / 180;

    const a =
      Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
      Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    const distance = R * c;
    return distance;
  }

  delay(ms: number) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }
  getPositionData(): Observable<any> {
    return this.locationSubject.asObservable();
  }
  stopTracking(): void {
    if (this.intervalSubscription && !this.intervalSubscription.closed) {
      this.intervalSubscription.unsubscribe();
    }
  }
}
